import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Types
export type AuthThemeMode = 'light' | 'dark';
export type AuthThemeColor = 'teal' | 'blue' | 'gray';

export interface AuthThemeConfig {
  mode: AuthThemeMode;
  backgroundColor: string;
  cardBackground: string;
  textColor: string;
}

export interface AuthThemeContextType {
  theme: AuthThemeConfig;
  setTheme: (theme: Partial<AuthThemeConfig>) => void;
  isAuthPage: boolean;
}

// Default theme configuration
const DEFAULT_AUTH_THEME: AuthThemeConfig = {
  mode: 'light',
  backgroundColor: 'rgb(0, 172, 193)', // Teal background
  cardBackground: 'rgb(255, 255, 255)', // White card background
  textColor: 'rgb(0, 0, 0)', // Black text
};

// Create context
const AuthThemeContext = createContext<AuthThemeContextType | undefined>(undefined);

// Add a dev helper to check for other theme providers
function isThemeProviderActive(): boolean {
  return !!document.documentElement.getAttribute('data-route-theme-active') ||
         !!document.documentElement.getAttribute('data-auth-theme-active');
}

/**
 * AuthThemeProvider
 *
 * Provides a dedicated theme context for authentication-related pages (e.g., login, register, forgot password).
 * Applies the specified auth theme to the DOM for its children and restores the previous theme on unmount.
 *
 * Development-Time Protection:
 * - Sets a marker attribute (data-auth-theme-active) on the document root when active.
 * - If multiple theme providers (AuthThemeProvider or RouteThemeProvider) are active at the same time,
 *   a warning is logged in the browser console:
 *     [Theme] Multiple theme providers detected! This may cause theme leakage or unexpected behavior.
 * - The marker is cleaned up on unmount.
 *
 * Usage:
 *   <AuthThemeProvider>
 *     <AuthLayout>...</AuthLayout>
 *   </AuthThemeProvider>
 */
// Provider component
export const AuthThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [theme, setThemeState] = useState<AuthThemeConfig>(DEFAULT_AUTH_THEME);
  
  // Check if current route is an auth page
  const isAuthPage = useCallback(() => {
    return location.pathname.startsWith('/auth');
  }, [location.pathname]);

  // Update theme
  const setTheme = useCallback((newTheme: Partial<AuthThemeConfig>) => {
    setThemeState(prev => ({
      ...prev,
      ...newTheme,
    }));
  }, []);

  // Apply theme to DOM
  useEffect(() => {
    if (isAuthPage()) {
      const root = document.documentElement;
      const body = document.body;

      // DEV WARNING: Check for multiple theme providers
      if (process.env.NODE_ENV === 'development') {
        if (isThemeProviderActive()) {
           
          console.warn('[Theme] Multiple theme providers detected! This may cause theme leakage or unexpected behavior.');
        }
        root.setAttribute('data-auth-theme-active', 'true');
      }

      // Remove any existing auth-related classes
      body.classList.remove('authentication-bg', 'authentication-bg-pattern');

      // Set theme attributes
      root.setAttribute('data-bs-theme', theme.mode);
      root.setAttribute('data-menu-color', theme.mode);
      root.setAttribute('data-topbar-color', theme.mode);

      // Set background color
      body.style.backgroundColor = theme.backgroundColor;

      // Cleanup on unmount
      return () => {
        body.style.backgroundColor = '';
        root.removeAttribute('data-bs-theme');
        root.removeAttribute('data-menu-color');
        root.removeAttribute('data-topbar-color');
        if (process.env.NODE_ENV === 'development') {
          root.removeAttribute('data-auth-theme-active');
        }
      };
    }
  }, [theme, isAuthPage]);

  const value = {
    theme,
    setTheme,
    isAuthPage: isAuthPage(),
  };

  return (
    <AuthThemeContext.Provider value={value}>
      {children}
    </AuthThemeContext.Provider>
  );
};

// Hook to use the auth theme context
export const useAuthTheme = () => {
  const context = useContext(AuthThemeContext);
  if (!context) {
    throw new Error('useAuthTheme must be used within an AuthThemeProvider');
  }
  return context;
}; 